import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, Upload, CloudDownload, Loader2, ArrowRightLeft } from "lucide-react";

/**
 * Admin-only page — copy every client/contract/PV/stock from another
 * environment (usually preview) into the current one (usually production).
 *
 * Three flows exposed:
 *  1. Export JSON — download a full dump from the CURRENT environment.
 *  2. Import JSON — upload a previously downloaded dump into the CURRENT env.
 *  3. Sync automat — give a source URL + admin creds and let the backend
 *     pull the dump for you (no manual file passing).
 */
export default function AdminSyncPage() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullForm, setPullForm] = useState({
    source_url: "https://business-portal-app-2.preview.emergentagent.com",
    email: "",
    password: "",
    overwrite: true,
  });
  const [lastStats, setLastStats] = useState(null);

  const exportDump = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/sync/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `imaggio-sync-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const counts = data?.counts || {};
      toast.success(`Export OK — ${Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Export a eșuat");
    } finally {
      setExporting(false);
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const dump = JSON.parse(text);
      const { data } = await api.post("/admin/sync/import", {
        collections: dump.collections || {},
        overwrite: true,
      });
      setLastStats(data.stats);
      toast.success("Import complet");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import a eșuat");
    } finally {
      setImporting(false);
    }
  };

  const pullFromSource = async (e) => {
    e.preventDefault();
    if (!pullForm.source_url || !pullForm.email || !pullForm.password) {
      toast.error("Completează toate câmpurile de mai sus.");
      return;
    }
    setPulling(true);
    try {
      const { data } = await api.post("/admin/sync/pull", pullForm);
      setLastStats(data.stats);
      toast.success("Sync automat complet");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Sync a eșuat");
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="admin-sync-page">
      <div>
        <h1 className="font-display text-3xl font-black text-slate-900">Sync date între medii</h1>
        <p className="text-sm text-slate-600 mt-1">
          Copiază toate datele (clienți, contracte, PV-uri, stocuri, mișcări, interacțiuni) dintr-un mediu în altul —
          util când vrei să aduci ce ai în <b>preview</b> pe <b>producție</b> (sau invers).
        </p>
      </div>

      {/* Flow 3 — Sync automat (recommended) */}
      <section className="border-2 border-gold rounded-lg p-6 bg-gold-soft/30" data-testid="sync-pull-section">
        <div className="flex items-center gap-2 mb-4">
          <ArrowRightLeft className="w-5 h-5 text-gold-deep" />
          <h2 className="font-display text-xl font-bold text-slate-900">Sync automat (recomandat)</h2>
        </div>
        <p className="text-xs text-slate-600 mb-4">
          Serverul curent se conectează la mediul-sursă, se loghează cu contul admin de mai jos și trage toate datele.
          Recomandat: rulează asta pe <b>{typeof window !== "undefined" ? window.location.host : "prod"}</b> ca să aduci datele
          din preview.
        </p>
        <form onSubmit={pullFromSource} className="space-y-4">
          <div>
            <Label className="label-eyebrow">URL mediu sursă</Label>
            <Input
              value={pullForm.source_url}
              onChange={(e) => setPullForm({ ...pullForm, source_url: e.target.value })}
              placeholder="https://business-portal-app-2.preview.emergentagent.com"
              className="mt-2 h-11"
              data-testid="sync-source-url"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Email admin pe sursă</Label>
              <Input
                value={pullForm.email}
                onChange={(e) => setPullForm({ ...pullForm, email: e.target.value })}
                placeholder="admin@imaggio2020tm.ro"
                className="mt-2 h-11"
                data-testid="sync-source-email"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Parolă admin pe sursă</Label>
              <Input
                type="password"
                value={pullForm.password}
                onChange={(e) => setPullForm({ ...pullForm, password: e.target.value })}
                className="mt-2 h-11"
                data-testid="sync-source-password"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={pullForm.overwrite}
              onChange={(e) => setPullForm({ ...pullForm, overwrite: e.target.checked })}
              data-testid="sync-overwrite-toggle"
            />
            Suprascrie documentele cu același ID (recomandat)
          </label>
          <Button
            type="submit"
            disabled={pulling}
            className="btn-gold hover:btn-gold"
            data-testid="sync-pull-btn"
          >
            {pulling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-2" />}
            {pulling ? "Se sincronizează..." : "Trage tot din sursa de mai sus"}
          </Button>
        </form>
      </section>

      {/* Flow 1 & 2 — manual export/import */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border rounded-lg p-5 bg-white" data-testid="sync-export-section">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-5 h-5 text-slate-700" />
            <h3 className="font-semibold text-slate-900">1. Export din mediul curent</h3>
          </div>
          <p className="text-xs text-slate-600 mb-4">
            Descarcă un fișier JSON cu tot ce e în DB-ul curent (tenant scope).
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={exportDump}
            disabled={exporting}
            data-testid="sync-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Descarcă JSON complet
          </Button>
        </div>

        <div className="border rounded-lg p-5 bg-white" data-testid="sync-import-section">
          <div className="flex items-center gap-2 mb-3">
            <Upload className="w-5 h-5 text-slate-700" />
            <h3 className="font-semibold text-slate-900">2. Import în mediul curent</h3>
          </div>
          <p className="text-xs text-slate-600 mb-4">
            Încarcă un JSON exportat anterior. Documentele existente cu același ID sunt suprascrise.
          </p>
          <label className="inline-flex items-center px-4 h-10 rounded-md border border-slate-300 bg-white text-sm cursor-pointer hover:bg-slate-50">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            <span>{importing ? "Se importă..." : "Alege fișier JSON"}</span>
            <input
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => importFile(e.target.files?.[0])}
              disabled={importing}
              data-testid="sync-import-file"
            />
          </label>
        </div>
      </section>

      {lastStats && (
        <section className="border rounded-lg p-5 bg-emerald-50 border-emerald-200" data-testid="sync-stats">
          <h3 className="font-semibold text-emerald-900 mb-3">Rezultat ultima sincronizare</h3>
          <table className="text-sm w-full">
            <thead>
              <tr className="text-left text-emerald-900">
                <th className="py-1">Colecție</th>
                <th className="py-1 text-right">Inserate</th>
                <th className="py-1 text-right">Actualizate</th>
                <th className="py-1 text-right">Ignorate</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(lastStats).map(([coll, s]) => (
                <tr key={coll} className="border-t border-emerald-200">
                  <td className="py-1 font-mono text-xs">{coll}</td>
                  <td className="py-1 text-right">{s.inserted}</td>
                  <td className="py-1 text-right">{s.updated}</td>
                  <td className="py-1 text-right">{s.skipped}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
